import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Lightning, 
  ArrowRight,
  Target,
  TrendUp,
  Barbell,
  Fire,
  CheckCircle,
  Crown,
  Leaf,
  Medal
} from "@phosphor-icons/react";

// Premium Images for backgrounds - REAL photos
const IMAGES = {
  welcome: "https://customer-assets.emergentagent.com/job_beginner-gains-app/artifacts/6lpkvb4m_0F8F7D0A-2BAA-4BEF-9903-E8B033455BB0.jpeg",
  level: "https://customer-assets.emergentagent.com/job_beginner-gains-app/artifacts/ecattype_F5717EB5-1B0D-4A1F-A1CB-23D101548C17.png",
  goal: "https://customer-assets.emergentagent.com/job_beginner-gains-app/artifacts/5d8ko8in_00083195-1271-40DA-B5F6-3FCB66958CC5.png",
  final: "https://static.prod-images.emergentagent.com/jobs/ad525726-9ac2-45ae-8c95-64b3bbaa5d43/images/22bd503f8101b02d8d0c7b2abed41d8368cf08e374802dac123ce8af5bd292e2.png",
  // Level-specific images
  principiante: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800&q=80",
  intermedio: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&q=80",
  avanzado: "https://images.unsplash.com/photo-1583454110551-21f2fa2afe61?w=800&q=80",
  goat: "https://images.unsplash.com/photo-1526506118085-60ce8714f8c5?w=800&q=80",
  // Track images - validated working URLs
  men: "https://images.unsplash.com/photo-1679384322287-1d89429cf674?w=800&q=80",
  women: "https://images.unsplash.com/photo-1518611012118-696072aa579a?w=800&q=80"
};

// ============ CONFIGURATION ============
const LEVELS = [
  {
    id: "principiante",
    name: "Principiante",
    description: "Empezando tu camino fitness",
    icon: Leaf,
    color: "#22C55E",
    image: IMAGES.principiante
  },
  {
    id: "intermedio", 
    name: "Intermedio",
    description: "Constancia establecida",
    icon: Barbell,
    color: "#3B82F6",
    image: IMAGES.intermedio
  },
  {
    id: "avanzado",
    name: "Avanzado",
    description: "Entrenamiento serio",
    icon: Fire,
    color: "#F97316",
    image: IMAGES.avanzado
  },
  {
    id: "goat",
    name: "GOAT",
    description: "Máximo rendimiento",
    icon: Crown,
    color: "#D4AF37",
    image: IMAGES.goat
  }
];

const GOALS = [
  {
    id: "definicion",
    name: "Definición",
    description: "Quemar grasa, mantener músculo",
    icon: Target,
    color: "#EF4444"
  },
  {
    id: "ganancia",
    name: "Ganancia Muscular",
    description: "Construir masa muscular",
    icon: TrendUp,
    color: "#22C55E"
  },
  {
    id: "recomposicion",
    name: "Recomposición",
    description: "Transformación total",
    icon: Fire,
    color: "#F97316"
  },
  {
    id: "rendimiento",
    name: "Rendimiento",
    description: "Fuerza y potencia",
    icon: Medal,
    color: "#8B5CF6"
  }
];

const TRACKS = [
  { id: "men", name: "Hombre", image: IMAGES.men },
  { id: "women", name: "Mujer", image: IMAGES.women }
];

// ============ BACKGROUND COMPONENT ============
const PremiumBackground = ({ image, children }) => (
  <div className="relative min-h-screen">
    {/* Background Image */}
    <div className="absolute inset-0 z-0">
      <img 
        src={image} 
        alt=""
        className="w-full h-full object-cover object-[center_30%] opacity-40"
      />
      {/* Gradient overlays for premium look */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]/40" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/60 to-transparent" />
    </div>
    {/* Content */}
    <div className="relative z-10">
      {children}
    </div>
  </div>
);

// ============ STEP COMPONENTS ============

const StepWelcome = ({ onNext }) => (
  <PremiumBackground image={IMAGES.welcome}>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center justify-center min-h-screen text-center px-6"
    >
      {/* Logo Badge */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: "spring" }}
        className="w-24 h-24 bg-[#D4AF37]/20 border-2 border-[#D4AF37] flex items-center justify-center mb-8 backdrop-blur-sm"
      >
        <Lightning weight="fill" className="w-12 h-12 text-[#D4AF37]" />
      </motion.div>

      {/* Title */}
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="font-['Outfit'] text-4xl sm:text-5xl font-black tracking-tighter uppercase text-white mb-4"
        style={{ textShadow: '0 4px 20px rgba(0,0,0,0.8)' }}
      >
        Bienvenido a <span className="text-[#D4AF37]">GOATGYM</span>
      </motion.h1>

      {/* Subtitle */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="max-w-sm"
      >
        <p className="text-white/70 text-lg leading-relaxed mb-2" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>
          No has llegado aquí por casualidad.
        </p>
        <p className="text-white font-bold text-xl" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>
          Aquí se construye disciplina, físico y mentalidad.
        </p>
      </motion.div>

      {/* CTA */}
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        whileHover={{ scale: 1.02, boxShadow: "0 0 40px rgba(212, 175, 55, 0.4)" }}
        whileTap={{ scale: 0.98 }}
        onClick={onNext}
        className="mt-12 group px-10 py-5 bg-[#D4AF37] text-black font-black text-base tracking-[0.15em] uppercase hover:bg-[#F0C74A] transition-all duration-300 flex items-center gap-3 shadow-lg shadow-[#D4AF37]/30"
        data-testid="onboarding-continue-btn"
      >
        <span>EMPEZAR</span>
        <ArrowRight weight="bold" className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      </motion.button>
    </motion.div>
  </PremiumBackground>
);

const StepLevel = ({ onNext, selectedLevel, setSelectedLevel }) => (
  <PremiumBackground image={IMAGES.level}>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col min-h-screen px-5 py-12"
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-6"
      >
        <p className="text-[#D4AF37] text-xs font-black tracking-[0.2em] uppercase mb-2">Paso 1 de 3</p>
        <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-black tracking-tight text-white" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.8)' }}>
          ¿Cuál es tu <span className="text-[#D4AF37]">nivel</span>?
        </h2>
      </motion.div>

      {/* Level Cards - Premium visual with images */}
      <div className="grid grid-cols-2 gap-3 flex-1 max-w-lg mx-auto w-full">
        {LEVELS.map((level, idx) => {
          const Icon = level.icon;
          const isSelected = selectedLevel === level.id;
          
          return (
            <motion.button
              key={level.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * idx }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedLevel(level.id)}
              className={`relative overflow-hidden h-36 sm:h-44 transition-all duration-300 ${
                isSelected
                  ? "ring-2 ring-[#D4AF37] shadow-lg shadow-[#D4AF37]/30"
                  : "ring-1 ring-white/10 hover:ring-white/30"
              }`}
              data-testid={`level-${level.id}`}
            >
              {/* Background Image */}
              <img 
                src={level.image} 
                alt={level.name}
                className={`absolute inset-0 w-full h-full object-cover transition-all duration-300 ${
                  isSelected ? 'opacity-60 scale-105' : 'opacity-40'
                }`}
              />
              
              {/* Overlay */}
              <div className={`absolute inset-0 transition-all duration-300 ${
                isSelected 
                  ? 'bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent' 
                  : 'bg-gradient-to-t from-[#0A0A0A]/90 via-[#0A0A0A]/70 to-[#0A0A0A]/40'
              }`} />

              {/* Content */}
              <div className="relative z-10 h-full flex flex-col justify-end p-4 text-left">
                {/* Selected indicator */}
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute top-3 right-3"
                  >
                    <CheckCircle weight="fill" className="w-6 h-6 text-[#D4AF37]" />
                  </motion.div>
                )}
                
                {/* Icon */}
                <div 
                  className="w-8 h-8 flex items-center justify-center mb-2"
                  style={{ color: isSelected ? level.color : 'rgba(255,255,255,0.6)' }}
                >
                  <Icon weight="duotone" className="w-6 h-6" />
                </div>
                
                <h3 className="font-black text-white text-lg leading-tight">{level.name}</h3>
                <p className="text-white/60 text-xs mt-1">{level.description}</p>
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* CTA */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: selectedLevel ? 1 : 0.5 }}
        disabled={!selectedLevel}
        whileHover={selectedLevel ? { scale: 1.02 } : {}}
        whileTap={selectedLevel ? { scale: 0.98 } : {}}
        onClick={onNext}
        className="mt-6 w-full max-w-lg mx-auto py-5 bg-[#D4AF37] text-black font-black text-sm tracking-[0.1em] uppercase hover:bg-[#F0C74A] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-[#D4AF37]/30"
        data-testid="onboarding-level-next-btn"
      >
        <span>CONTINUAR</span>
        <ArrowRight weight="bold" className="w-5 h-5" />
      </motion.button>
    </motion.div>
  </PremiumBackground>
);

const StepTrack = ({ onNext, selectedTrack, setSelectedTrack }) => (
  <PremiumBackground image={IMAGES.level}>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col min-h-screen px-5 py-12"
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-6"
      >
        <p className="text-[#D4AF37] text-xs font-black tracking-[0.2em] uppercase mb-2">Paso 2 de 3</p>
        <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-black tracking-tight text-white" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.8)' }}>
          Selecciona tu <span className="text-[#D4AF37]">programa</span>
        </h2>
      </motion.div>

      {/* Track Cards - Premium visual with real images */}
      <div className="grid grid-cols-2 gap-4 flex-1 max-w-md mx-auto w-full items-center">
        {TRACKS.map((track, idx) => {
          const isSelected = selectedTrack === track.id;
          
          return (
            <motion.button
              key={track.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * idx }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedTrack(track.id)}
              className={`relative overflow-hidden h-52 sm:h-64 transition-all duration-300 ${
                isSelected
                  ? "ring-2 ring-[#D4AF37] shadow-lg shadow-[#D4AF37]/30"
                  : "ring-1 ring-white/10 hover:ring-white/30"
              }`}
              data-testid={`track-${track.id}`}
            >
              {/* Background Image */}
              <img 
                src={track.image} 
                alt={track.name}
                className={`absolute inset-0 w-full h-full object-cover object-top transition-all duration-300 ${
                  isSelected ? 'opacity-70 scale-105' : 'opacity-50'
                }`}
              />
              
              {/* Overlay */}
              <div className={`absolute inset-0 transition-all duration-300 ${
                isSelected 
                  ? 'bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent' 
                  : 'bg-gradient-to-t from-[#0A0A0A]/90 via-[#0A0A0A]/60 to-[#0A0A0A]/30'
              }`} />

              {/* Content */}
              <div className="relative z-10 h-full flex flex-col justify-end p-5 text-center">
                {/* Selected indicator */}
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute top-3 right-3"
                  >
                    <CheckCircle weight="fill" className="w-7 h-7 text-[#D4AF37]" />
                  </motion.div>
                )}
                
                <h3 className="font-black text-white text-2xl">{track.name}</h3>
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* CTA */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: selectedTrack ? 1 : 0.5 }}
        disabled={!selectedTrack}
        whileHover={selectedTrack ? { scale: 1.02 } : {}}
        whileTap={selectedTrack ? { scale: 0.98 } : {}}
        onClick={onNext}
        className="mt-6 w-full max-w-lg mx-auto py-5 bg-[#D4AF37] text-black font-black text-sm tracking-[0.1em] uppercase hover:bg-[#F0C74A] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-[#D4AF37]/30"
        data-testid="onboarding-track-next-btn"
      >
        <span>CONTINUAR</span>
        <ArrowRight weight="bold" className="w-5 h-5" />
      </motion.button>
    </motion.div>
  </PremiumBackground>
);

const StepGoal = ({ onNext, selectedGoal, setSelectedGoal }) => (
  <PremiumBackground image={IMAGES.goal}>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col min-h-screen px-5 py-12"
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-6"
      >
        <p className="text-[#D4AF37] text-xs font-black tracking-[0.2em] uppercase mb-2">Paso 3 de 3</p>
        <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-black tracking-tight text-white" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.8)' }}>
          ¿Cuál es tu <span className="text-[#D4AF37]">objetivo</span>?
        </h2>
      </motion.div>

      {/* Goal Cards */}
      <div className="space-y-3 flex-1 max-w-lg mx-auto w-full">
        {GOALS.map((goal, idx) => {
          const Icon = goal.icon;
          const isSelected = selectedGoal === goal.id;
          
          return (
            <motion.button
              key={goal.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 * idx }}
              whileTap={{ scale: 0.99 }}
              onClick={() => setSelectedGoal(goal.id)}
              className={`relative w-full p-4 transition-all duration-300 flex items-center gap-4 backdrop-blur-sm ${
                isSelected
                  ? "bg-[#D4AF37]/20 ring-2 ring-[#D4AF37] shadow-lg shadow-[#D4AF37]/20"
                  : "bg-[#0A0A0A]/60 ring-1 ring-white/10 hover:ring-white/30"
              }`}
              data-testid={`goal-${goal.id}`}
            >
              {/* Icon */}
              <div 
                className={`w-12 h-12 flex items-center justify-center transition-all ${
                  isSelected ? 'bg-[#D4AF37]/30' : 'bg-white/10'
                }`}
              >
                <Icon 
                  weight="duotone" 
                  className="w-6 h-6"
                  style={{ color: isSelected ? goal.color : 'rgba(255,255,255,0.6)' }}
                />
              </div>

              {/* Content */}
              <div className="flex-1 text-left">
                <h3 className="font-black text-white text-lg">{goal.name}</h3>
                <p className="text-white/60 text-xs">{goal.description}</p>
              </div>

              {/* Selected indicator */}
              {isSelected && (
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
                  <CheckCircle weight="fill" className="w-6 h-6 text-[#D4AF37]" />
                </motion.div>
              )}
            </motion.button>
          );
        })}
      </div>

      {/* CTA */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: selectedGoal ? 1 : 0.5 }}
        disabled={!selectedGoal}
        whileHover={selectedGoal ? { scale: 1.02 } : {}}
        whileTap={selectedGoal ? { scale: 0.98 } : {}}
        onClick={onNext}
        className="mt-6 w-full max-w-lg mx-auto py-5 bg-[#D4AF37] text-black font-black text-sm tracking-[0.1em] uppercase hover:bg-[#F0C74A] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-[#D4AF37]/30"
        data-testid="onboarding-goal-next-btn"
      >
        <span>FINALIZAR</span>
        <ArrowRight weight="bold" className="w-5 h-5" />
      </motion.button>
    </motion.div>
  </PremiumBackground>
);

const StepFinal = ({ onComplete }) => (
  <PremiumBackground image={IMAGES.final}>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center justify-center min-h-screen text-center px-6"
    >
      {/* Animated checkmark */}
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
        className="w-28 h-28 bg-[#D4AF37]/20 border-2 border-[#D4AF37] flex items-center justify-center mb-8 backdrop-blur-sm"
      >
        <CheckCircle weight="fill" className="w-14 h-14 text-[#D4AF37]" />
      </motion.div>

      {/* Title */}
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="font-['Outfit'] text-3xl sm:text-4xl font-black tracking-tighter uppercase text-white mb-4"
        style={{ textShadow: '0 4px 20px rgba(0,0,0,0.8)' }}
      >
        Tu transformación<br />
        <span className="text-[#D4AF37]">empieza hoy.</span>
      </motion.h1>

      {/* Subtitle */}
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="text-white/70 text-xl font-bold"
        style={{ textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}
      >
        Constancia {">"} Motivación
      </motion.p>

      {/* Decorative line */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ delay: 0.8, duration: 0.6 }}
        className="w-24 h-1 bg-[#D4AF37] my-10"
      />

      {/* CTA */}
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
        whileHover={{ scale: 1.02, boxShadow: "0 0 50px rgba(212, 175, 55, 0.5)" }}
        whileTap={{ scale: 0.98 }}
        onClick={onComplete}
        className="group px-10 py-5 bg-[#D4AF37] text-black font-black text-base tracking-[0.15em] uppercase hover:bg-[#F0C74A] transition-all duration-300 flex items-center gap-3 shadow-xl shadow-[#D4AF37]/40"
        data-testid="onboarding-complete-btn"
      >
        <span>ENTRAR A GOATGYM</span>
        <ArrowRight weight="bold" className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      </motion.button>
    </motion.div>
  </PremiumBackground>
);

// ============ MAIN COMPONENT ============
const OnboardingFlow = ({ onComplete, initialStep = 0 }) => {
  const [step, setStep] = useState(initialStep);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [selectedTrack, setSelectedTrack] = useState(null);

  const handleComplete = () => {
    // Save preferences including level and track for program assignment
    const preferences = {
      level: selectedLevel,
      goal: selectedGoal,
      track: selectedTrack,
      completedAt: new Date().toISOString()
    };
    localStorage.setItem("goatgym_onboarding", JSON.stringify(preferences));
    
    if (onComplete) {
      onComplete(preferences);
    }
  };

  // Reorder steps: Level (1) -> Track/Sexo (2) -> Goal (3) -> Final
  return (
    <div 
      className="fixed inset-0 z-50 bg-[#0A0A0A] overflow-y-auto"
      style={{ fontFamily: "'Manrope', sans-serif" }}
      data-testid="onboarding-flow"
    >
      {/* Google Fonts */}
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&family=Manrope:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-[#1A1A1A] z-20">
        <motion.div
          className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F0C74A]"
          initial={{ width: "0%" }}
          animate={{ width: `${((step + 1) / 5) * 100}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Steps: Welcome -> Level -> Track -> Goal -> Final */}
      <AnimatePresence mode="wait">
        {step === 0 && (
          <StepWelcome 
            key="welcome" 
            onNext={() => setStep(1)} 
          />
        )}
        {step === 1 && (
          <StepLevel 
            key="level"
            onNext={() => setStep(2)}
            selectedLevel={selectedLevel}
            setSelectedLevel={setSelectedLevel}
          />
        )}
        {step === 2 && (
          <StepTrack 
            key="track"
            onNext={() => setStep(3)}
            selectedTrack={selectedTrack}
            setSelectedTrack={setSelectedTrack}
          />
        )}
        {step === 3 && (
          <StepGoal 
            key="goal"
            onNext={() => setStep(4)}
            selectedGoal={selectedGoal}
            setSelectedGoal={setSelectedGoal}
          />
        )}
        {step === 4 && (
          <StepFinal 
            key="final"
            onComplete={handleComplete}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default OnboardingFlow;
